import { diff as jsondiffpatchDiff } from 'jsondiffpatch';
import {
  groundOpticsTierIndexForDiff,
  highAltitudeOpticsTierIndexForDiff,
} from '@/lib/utils/optics-paliers';
import { stripTfsPrefixFromUnitDisplayName } from '@/lib/utils/unit-name-for-diff';
import { canonicalizeFactoryDescriptor } from '@/lib/utils/factory-descriptor';
import { coalitionFromRawGameStringWithDefault } from '@/lib/utils/coalition-from-raw';
import type { UnitWithWeapons } from './unit-weapons-joined';

/**
 * Diff structure from jsondiffpatch
 */
export type DiffResult = any;

/**
 * Array diff element types from jsondiffpatch
 */
export type ArrayDiffElement = ['~', any] | ['-', any] | ['+', any] | [' ', any];

/**
 * Unit change information
 */
export interface UnitChange {
  unit: UnitWithWeapons;
  diff: DiffResult;
  changeType: 'added' | 'removed' | 'changed';
}

/**
 * Patch diff result containing all changes
 */
export interface PatchDiffResult {
  added: UnitWithWeapons[];
  removed: UnitWithWeapons[];
  changed: Array<{
    unit: UnitWithWeapons;
    oldUnit: UnitWithWeapons;
    diff: DiffResult;
  }>;
}

/**
 * Create a normalized unit object for comparison (sorted arrays, etc.)
 */
function normalizeUnitForDiff(unit: UnitWithWeapons): any {
  return {
    ...unit,
    name: stripTfsPrefixFromUnitDisplayName(unit.name),
    factoryDescriptor: canonicalizeFactoryDescriptor(unit.factoryDescriptor),
    // OpticalStrength GRU rescales across patches; compare semantic tier so uniform rescale vanishes.
    optics: groundOpticsTierIndexForDiff(unit.optics),
    airOptics: highAltitudeOpticsTierIndexForDiff(unit.airOptics),
    lowAltOptics:
      unit.lowAltOptics !== undefined && unit.lowAltOptics !== null
        ? groundOpticsTierIndexForDiff(unit.lowAltOptics)
        : unit.lowAltOptics,
    // Sort arrays to ensure consistent comparison
    specialities: [...(unit.specialities || [])].sort(),
    weapons: [...(unit.weapons || [])].sort((a, b) => a.salvoIndex - b.salvoIndex),
    divisions: [...(unit.divisions || [])].sort(),
    occupiableTerrains: [...(unit.occupiableTerrains || [])].sort(),
    unitType: unit.unitType
      ? {
          ...unit.unitType,
          coalition: coalitionFromRawGameStringWithDefault(
            String(unit.unitType.coalition ?? '')
          ),
        }
      : unit.unitType,
  };
}

/**
 * Calculate diff between two versions of units with weapons
 * @param oldUnits Array of units from old version (with weapons joined)
 * @param newUnits Array of units from new version (with weapons joined)
 * @returns Patch diff result with added, removed, and changed units
 */
export function calculatePatchDiff(
  oldUnits: UnitWithWeapons[],
  newUnits: UnitWithWeapons[]
): PatchDiffResult {
  // Create maps for quick lookup
  const oldUnitsMap = new Map<string, UnitWithWeapons>();
  const newUnitsMap = new Map<string, UnitWithWeapons>();

  oldUnits.forEach((unit) => {
    oldUnitsMap.set(unit.descriptorName, unit);
  });

  newUnits.forEach((unit) => {
    newUnitsMap.set(unit.descriptorName, unit);
  });

  const added: UnitWithWeapons[] = [];
  const removed: UnitWithWeapons[] = [];
  const changed: Array<{
    unit: UnitWithWeapons;
    oldUnit: UnitWithWeapons;
    diff: DiffResult;
  }> = [];

  // Find added units (in new but not in old)
  newUnitsMap.forEach((unit, descriptorName) => {
    if (!oldUnitsMap.has(descriptorName)) {
      added.push(unit);
    }
  });

  // Find removed units (in old but not in new)
  oldUnitsMap.forEach((unit, descriptorName) => {
    if (!newUnitsMap.has(descriptorName)) {
      removed.push(unit);
    }
  });

  // Find changed units (in both but different)
  newUnitsMap.forEach((newUnit, descriptorName) => {
    const oldUnit = oldUnitsMap.get(descriptorName);
    if (oldUnit) {
      // Normalize units for comparison
      const normalizedOld = normalizeUnitForDiff(oldUnit);
      const normalizedNew = normalizeUnitForDiff(newUnit);

      // Calculate diff
      const unitDiff = jsondiffpatchDiff(normalizedOld, normalizedNew);

      // If there's a diff, the unit has changed
      if (unitDiff && Object.keys(unitDiff).length > 0) {
        changed.push({
          unit: newUnit,
          oldUnit,
          diff: unitDiff,
        });
      }
    }
  });

  return {
    added,
    removed,
    changed,
  };
}

/**
 * Check if a diff element represents an array addition
 */
export function isArrayDiffAdded(element: any): element is ['+', any] {
  return Array.isArray(element) && element.length === 2 && element[0] === '+';
}

/**
 * Check if a diff element represents an array removal
 */
export function isArrayDiffRemoved(element: any): element is ['-', any] {
  return Array.isArray(element) && element.length === 2 && element[0] === '-';
}

/**
 * Check if a diff element represents an array modification
 * Format: ['~', index, diff] (3 elements)
 */
export function isArrayDiffModified(element: any): element is ['~', number, any] {
  return Array.isArray(element) && element.length >= 2 && element[0] === '~';
}

/**
 * Check if a value is a diff object
 * jsondiffpatch uses [oldValue, newValue] arrays for simple changes
 * Custom formatters may use { __old, __new } objects
 */
export function isDiff(value: any): value is { __old: any; __new: any } {
  // jsondiffpatch format: [oldValue, newValue]
  if (Array.isArray(value) && value.length === 2) {
    return true;
  }
  // Custom format: { __old, __new }
  return (
    value &&
    typeof value === 'object' &&
    '__old' in value &&
    '__new' in value
  );
}

/**
 * Extract old and new values from a jsondiffpatch diff
 */
export function extractDiffValues(diffValue: any): { oldValue: any; newValue: any } | null {
  // jsondiffpatch format: [oldValue, newValue]
  if (Array.isArray(diffValue) && diffValue.length === 2) {
    return { oldValue: diffValue[0], newValue: diffValue[1] };
  }
  // Custom format: { __old, __new }
  if (diffValue && typeof diffValue === "object" && "__old" in diffValue && "__new" in diffValue) {
    return { oldValue: diffValue.__old, newValue: diffValue.__new };
  }
  return null;
}

/**
 * Check if a value is a new-only diff (only has __new)
 */
export function isNewDiff(value: any): value is { __new: any } {
  return (
    value &&
    typeof value === 'object' &&
    '__new' in value &&
    !('__old' in value)
  );
}

/**
 * Check if a value is an old-only diff (only has __old)
 */
export function isOldDiff(value: any): value is { __old: any } {
  return (
    value &&
    typeof value === 'object' &&
    '__old' in value &&
    !('__new' in value)
  );
}

/** jsondiffpatch array filter uses `3` for position moves */
const JSONDIFFPATCH_ARRAY_MOVE = 3;

function flattenObjectDiffToChanges(
  obj: Record<string, unknown>,
  basePath: string
): Array<{ path: string; oldValue: unknown; newValue: unknown }> {
  const out: Array<{ path: string; oldValue: unknown; newValue: unknown }> = [];
  for (const [k, v] of Object.entries(obj)) {
    const p = `${basePath}.${k}`;
    const pair = extractDiffValues(v);
    if (pair) {
      out.push({ path: p, oldValue: pair.oldValue, newValue: pair.newValue });
      continue;
    }
    if (v && typeof v === 'object' && v !== null && !Array.isArray(v)) {
      const rec = v as Record<string, unknown>;
      if (rec._t === 'a') {
        out.push(...expandJsonDiffpatchArrayDeltaToChanges(rec, p));
      } else {
        out.push(...flattenObjectDiffToChanges(rec, p));
      }
    }
  }
  return out;
}

/**
 * Turn a jsondiffpatch array delta `{ _t: "a", _0: [...], 1: [...] }` into leaf path rows * so patch UI can render trait / array edits (the generic `search` path misses these).
 */
export function expandJsonDiffpatchArrayDeltaToChanges(
  delta: Record<string, unknown>,
  basePath: string
): Array<{ path: string; oldValue: unknown; newValue: unknown }> {
  const out: Array<{ path: string; oldValue: unknown; newValue: unknown }> = [];
  if (!delta || delta._t !== 'a') {
    return out;
  }

  for (const key of Object.keys(delta)) {
    if (key === '_t') continue;
    const item = delta[key] as unknown;

    if (key.startsWith('_')) {
      const idx = Number.parseInt(key.slice(1), 10);
      if (Number.isNaN(idx) || !Array.isArray(item) || item.length < 3) {
        continue;
      }
      const tag = item[2];
      if (tag === 0) {
        out.push({
          path: `${basePath}[${idx}]`,
          oldValue: item[0],
          newValue: undefined,
        });
      } else if (tag === JSONDIFFPATCH_ARRAY_MOVE) {
        const movedVal = item[0];
        const toIdx = item[1];
        out.push({
          path: `${basePath}[${idx}]`,
          oldValue: movedVal === '' ? '(reordered)' : movedVal,
          newValue: `Index ${toIdx}`,
        });
      }
      continue;
    }

    const idx = Number.parseInt(key, 10);
    if (Number.isNaN(idx)) {
      continue;
    }

    if (Array.isArray(item)) {
      if (item.length === 1) {
        out.push({
          path: `${basePath}[${idx}]`,
          oldValue: undefined,
          newValue: item[0],
        });
        continue;
      }
      const pair = extractDiffValues(item);
      if (pair) {
        out.push({
          path: `${basePath}[${idx}]`,
          oldValue: pair.oldValue,
          newValue: pair.newValue,
        });
        continue;
      }
    } else if (item && typeof item === 'object' && !Array.isArray(item)) {
      const rec = item as Record<string, unknown>;
      if (rec._t === 'a') {
        out.push(
          ...expandJsonDiffpatchArrayDeltaToChanges(rec, `${basePath}[${idx}]`)
        );
      } else {
        out.push(
          ...flattenObjectDiffToChanges(rec, `${basePath}[${idx}]`)
        );
      }
    }
  }

  return out;
}

